// Customer complaint detail — reuses ticket thread pattern but with severity/triage badges.
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, X, Sparkles, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import SendbirdEmbeddedChannel from "../components/sendbird/SendbirdEmbeddedChannel";
import { api } from "../api/client";

const STATUS_LABEL = {
    open: "Open",
    awaiting_agent: "Awaiting agent",
    awaiting_customer: "Awaiting your reply",
    in_progress: "In progress",
    resolved: "Resolved",
    closed: "Closed",
};

const SEVERITY_TONE = {
    low: "border-slate-200 bg-slate-50 text-slate-700",
    medium: "border-amber-200 bg-amber-50 text-amber-800",
    high: "border-orange-200 bg-orange-50 text-orange-800",
    critical: "border-rose-300 bg-rose-50 text-rose-800",
};

const ComplaintDetailContent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const threadRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/support/tickets/${id}`);
            setTicket(data);
            setMessages(data.messages || []);
        } catch (err) {
            if (err?.response?.status === 404) {
                toast.error("Complaint not found");
                navigate("/complaints");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [id]);

    // Poll every 20s
    useEffect(() => {
        const t = setInterval(load, 20_000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [id]);

    useEffect(() => {
        if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }, [messages]);

    const send = async () => {
        const content = reply.trim();
        if (!content || sending) return;
        try {
            setSending(true);
            await api.post(`/support/tickets/${id}/messages`, { content });
            setReply("");
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not send");
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return <div className="h-96 skeleton rounded-3xl" />;
    }
    if (!ticket) return null;

    const sev = ticket.severity_admin || ticket.severity_auto || "medium";
    const triage = ticket.ai_triage || {};
    const isClosed = ticket.status === "closed";

    return (
        <div className="rounded-3xl border border-zinc-100 bg-white shadow-soft" data-testid="complaint-thread">
            <div className="border-b border-zinc-100 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-xs text-zinc-400">{ticket.ticket_no}</span>
                            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-700">
                                {STATUS_LABEL[ticket.status] || ticket.status}
                            </span>
                            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${SEVERITY_TONE[sev]}`}>
                                {sev} severity
                            </span>
                            {ticket.complaint_subtype && (
                                <span className="rounded-full bg-zinc-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-600">
                                    {ticket.complaint_subtype.replace(/_/g, " ")}
                                </span>
                            )}
                        </div>
                        <h2 className="mt-1.5 text-lg font-semibold text-zinc-900">{ticket.subject}</h2>
                    </div>
                </div>

                {triage.suggested_resolution_path?.length > 0 && (
                    <div className="mt-4 rounded-2xl border border-brand-200 bg-brand-50/40 p-4" data-testid="complaint-ai-summary">
                        <div className="mb-2 flex items-center gap-1.5">
                            <Sparkles className="h-3.5 w-3.5 text-brand-700" />
                            <p className="text-xs font-semibold uppercase tracking-wider text-brand-700">AI-suggested path</p>
                        </div>
                        <ul className="space-y-1 text-xs text-zinc-700">
                            {triage.suggested_resolution_path.slice(0, 5).map((step, i) => (
                                <li key={i}>• {step}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {ticket.compensation_issued && (
                    <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-800" data-testid="complaint-compensation-badge">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Compensation issued: ₹{(ticket.compensation_total || 0).toFixed(0)}
                    </div>
                )}
            </div>

            {/* Phase 2b — Sendbird embed for real-time support chat.
                Every ticket in this collection has been AI-triage-escalated (handoff=true)
                so the embed shows unconditionally when the ticket is open. */}
            {!isClosed && (
                <div className="border-b border-zinc-100 p-5 pb-6" data-testid="complaint-sendbird-section">
                    <div className="mb-3 flex items-center justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Live chat</p>
                            <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Talk to PILLS Care</h3>
                        </div>
                        <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                            Real-time
                        </span>
                    </div>
                    <SendbirdEmbeddedChannel
                        channelUrl={`pills_support_${ticket.id}`}
                        customType="support_ticket"
                        height="440px"
                        testId="sendbird-support-embed-customer"
                        emptyMessage="Connecting you to PILLS Care…"
                    />
                </div>
            )}

            {/* Thread */}
            <div ref={threadRef} className="h-[440px] space-y-3 overflow-y-auto bg-zinc-50/40 p-5" data-testid="complaint-thread-messages">
                {messages.map((m) => {
                    const isCustomer = m.sender_type === "customer";
                    const isSystem = m.sender_type === "system";
                    if (isSystem) {
                        return (
                            <div key={m.id} className="my-2 text-center" data-testid={`complaint-msg-${m.id}`}>
                                <span className="rounded-full bg-zinc-200 px-3 py-0.5 text-[10px] uppercase tracking-wider text-zinc-600">
                                    {m.content}
                                </span>
                            </div>
                        );
                    }
                    return (
                        <div key={m.id} className={`flex ${isCustomer ? "justify-end" : "justify-start"}`} data-testid={`complaint-msg-${m.id}`}>
                            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                                isCustomer ? "bg-brand-700 text-white" : "bg-white text-zinc-900 ring-1 ring-zinc-100"
                            }`}>
                                <p className="whitespace-pre-wrap break-words">{m.content}</p>
                                <p className={`mt-1 text-[10px] ${isCustomer ? "text-white/70" : "text-zinc-400"}`}>
                                    {m.sender_name || (isCustomer ? "You" : "PILLS")} · {new Date(m.created_at).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {!isClosed && (
                <div className="border-t border-zinc-100 p-4">
                    <div className="flex items-end gap-2">
                        <textarea
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }}
                            placeholder="Type a reply… (Ctrl+Enter to send)"
                            rows={2}
                            maxLength={4000}
                            className="flex-1 resize-none rounded-2xl border border-zinc-200 p-3 text-sm outline-none focus:border-brand-500"
                            data-testid="complaint-reply-input"
                        />
                        <button
                            onClick={send}
                            disabled={!reply.trim() || sending}
                            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-700 text-white shadow-sm hover:bg-brand-800 disabled:opacity-50"
                            aria-label="Send"
                            data-testid="complaint-send-btn"
                        >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const ComplaintDetail = () => (
    <Layout>
        <section className="mx-auto max-w-4xl px-6 py-10 lg:px-12">
            <Link to="/complaints" className="mb-3 inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-brand-700" data-testid="complaint-back-link">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to complaints
            </Link>
            <AuthGuard>
                <ComplaintDetailContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default ComplaintDetail;
